import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PlanCheckPipe } from '@app/pipes/plan-check.pipe';
import { TrackingService } from '@app/services/tracking.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DepartmentVariationApi } from '@reducers/orm/department-variation/department-variation.api';
import {
  DEFAULT_SETTINGS_VARIATION_ID,
  DepartmentVariationSection,
  DepartmentVariationSettingModel,
} from '@reducers/orm/department-variation/department-variation.model';
import { DIALOG_DATA, DialogActivationComponent, DialogRef } from '@sb/ui/dialog';
import { InputModule } from '@sb/ui/input';
import { RadioBadgeComponent } from '@sb/ui/radio-badge';
import { RadioBadgeGroupComponent } from '@sb/ui/radio-badge-group';
import { PlanType } from '@shiftbase-com/ts-shared/models';
import { BehaviorSubject, filter, finalize, map, switchMap, take } from 'rxjs';

import { OnboardingChapterModule } from '../components/chapter.module';
import { OptimizedScheduleDialogStep } from './optimized-schedule.model';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'optimized-schedule-dialog',
  imports: [
    DialogActivationComponent,
    FormsModule,
    NgStyle,
    TranslatePipe,
    OnboardingChapterModule,
    InputModule,
    RadioBadgeComponent,
    RadioBadgeGroupComponent,
  ],
  providers: [PlanCheckPipe],
  templateUrl: './optimized-schedule-dialog.component.html',
})
export class OptimizedScheduleDialogComponent {
  eventData = inject<{
    originator: string;
  }>(DIALOG_DATA);
  private dialogRef = inject(DialogRef);
  private trackingService = inject(TrackingService);
  private departmentVariationAPI = inject(DepartmentVariationApi);
  private planCheck = inject(PlanCheckPipe);
  private translate = inject(TranslateService);

  OptimizedScheduleDialogStep = OptimizedScheduleDialogStep;
  currentStep = OptimizedScheduleDialogStep.TEASER;
  steps = 2;

  availabilityWeekAmount = '1';
  availabilityWeekAmountOptions: { value: string; label: string }[] = [
    { value: '1', label: this.translate.instant('week') },
    { value: '2', label: this.translate.instant('2 weeks') },
    { value: '3', label: this.translate.instant('3 weeks') },
    { value: '4', label: this.translate.instant('4 weeks') },
  ];

  nextButtonLoading = false;

  hasPremium: boolean;

  private scheduleAvailabilitySettings: Partial<DepartmentVariationSettingModel>;
  private settingsLoaded$ = new BehaviorSubject<boolean>(false);
  private eventTracked = false;

  constructor() {
    void this.getDefaultVariationSettings(DepartmentVariationSection.SCHEDULE_AVAILABILITY)
      .pipe(take(1))
      .subscribe((availabilitySettings) => {
        this.scheduleAvailabilitySettings = availabilitySettings;
        this.settingsLoaded$.next(true);
      });

    this.hasPremium = this.planCheck.transform(PlanType.PREMIUM);
    this.steps = this.hasPremium ? 3 : 2;
  }

  goToStep(step: OptimizedScheduleDialogStep) {
    if (this.currentStep === OptimizedScheduleDialogStep.TEASER && !this.eventTracked) {
      this.trackingService.event('Onboarding Optimized Scheduling Started', this.eventData);
      this.eventTracked = true;
    }

    if (step === OptimizedScheduleDialogStep.EMPLOYEE_AVAILABILITY && !this.hasPremium) {
      // Skip employee availability settings step for non-premium accounts
      this.dialogRef.close({ action: 'primary' });
      return;
    }

    this.currentStep = step;
  }

  goBack() {
    this.currentStep = this.currentStep - 1;
  }

  savePreferencesAndClose() {
    this.nextButtonLoading = true;

    void this.settingsLoaded$
      .pipe(
        filter((loaded) => loaded),
        switchMap(() => this.saveScheduleAvailabilitySettings()),
        take(1),
        finalize(() => {
          this.nextButtonLoading = false;
        }),
      )
      .subscribe(() => {
        this.dialogRef.close({ action: 'primary' });
      });
  }

  private saveScheduleAvailabilitySettings() {
    const availabilityWeekAmount = Number(this.availabilityWeekAmount);

    const scheduleAvailabilitySettings: Partial<DepartmentVariationSettingModel> = {
      ...this.scheduleAvailabilitySettings,
      lock_availability_days_before_period: String(availabilityWeekAmount * 7),
      reminder_days_before: String((availabilityWeekAmount + 1) * 7),
      send_availability_reminder: true,
    };

    return this.departmentVariationAPI.add(
      scheduleAvailabilitySettings,
      DEFAULT_SETTINGS_VARIATION_ID,
      DepartmentVariationSection.SCHEDULE_AVAILABILITY,
    );
  }

  private getDefaultVariationSettings(section: DepartmentVariationSection) {
    return this.departmentVariationAPI.load(section).pipe(
      filter((variations) => variations?.length > 0),
      map((variations) => variations.find((variation) => variation.department_id === DEFAULT_SETTINGS_VARIATION_ID)),
      take(1),
    );
  }
}
